import React, { useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { makeStyles } from 'tss-react/mui';
import { keyframes } from 'tss-react';
import { IconButton } from '@mui/material';
import {
  Close as CloseIcon,
  Remove as MinimizeIcon,
  CropSquare as MaximizeIcon,
  FilterNone as RestoreIcon,
} from '@mui/icons-material';

// =============================================================================
// Window — the generic desktop-style window CHROME. Knows nothing about IM; it
// wraps arbitrary children in a draggable/resizable frame with a title bar
// (drag handle + minimize / maximize-restore / close), focus ring, open/close/
// maximize animations, viewport clamping, and keyboard shortcuts (Esc closes if
// allowed; Ctrl/Cmd+W closes). All state lives in the window manager; this is a
// controlled view that reports drag/resize back via onSetRect.
//
// Layout reserves space for the app's TopBar (top) when maximized so the app
// chrome stays visible. The minimized-window dock (Taskbar) now lives inside the
// chat column, not the viewport bottom, so maximized windows no longer reserve
// space for it — just a small bottom margin.
// =============================================================================

export const TOPBAR_H = 56; // app TopBar height — maximized windows start below it
export const TASKBAR_H = 32; // dock row height (min); used by Taskbar's layout
const EDGE_MARGIN = 8; // keep at least this much of a window on-screen

const openAnim = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to   { opacity: 1; transform: scale(1); }
`;

const useStyles = makeStyles()((theme) => ({
  // The Rnd-controlled frame. Animations target this via the wrapper className.
  frame: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    background: theme.palette.custom.chatBg,
    border: `1px solid ${theme.palette.custom.lineStrong}`,
    boxShadow: '0 10px 34px rgba(0,0,0,0.5)',
    fontFamily: theme.palette.custom.fontFamily,
    color: theme.palette.custom.ink,
    animation: `${openAnim} 0.18s ease`,
    // A maximize/restore transition on geometry. react-rnd writes inline
    // width/height/transform; transitioning them gives a smooth max/restore.
    transition: 'box-shadow 0.15s ease',
    outline: 'none',
  },
  // Focused window: brighter border + accent ring so the active window reads.
  frameFocused: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px ${theme.palette.primary.main}`,
  },
  // Smooth maximize/restore: animate the Rnd wrapper's geometry briefly. Added
  // only during the transition (removed after) so live dragging stays snappy.
  animating: {
    transition: 'all 0.2s ease !important',
  },
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0.5, 0.5, 1.25),
    background: theme.palette.custom.accentWash,
    borderBottom: `1px solid ${theme.palette.custom.line}`,
    cursor: 'grab',
    userSelect: 'none',
    flexShrink: 0,
    '&:active': { cursor: 'grabbing' },
  },
  // Attention blink (e.g. unread IM) — driven by options.attention on the window.
  titleBarAttention: {
    animation: `${keyframes`
      0%, 100% { background: ${theme.palette.custom.accentWash}; }
      50%      { background: ${theme.palette.custom.ember}; }
    `} 0.8s steps(1) infinite`,
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    fontSize: '0.85rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: 0,
    flexShrink: 0,
  },
  ctrlBtn: {
    padding: 3,
    color: theme.palette.custom.inkDim,
    borderRadius: 4,
    '& svg': { fontSize: '0.95rem' },
    '&:hover': { color: theme.palette.custom.ink, background: theme.palette.custom.hoverOverlay },
  },
  // The close button gets a red hover (desktop convention).
  closeBtn: {
    '&:hover': { color: '#fff', background: theme.palette.error.main },
  },
  // Body fills the rest; content is responsible for its own scroll.
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },

  // ===========================================================================
  // CLASSIC ("Luna") chrome — opted in via options.skin === 'classic'. A retro
  // Windows-XP frame: 3D beige border, blue gradient caption with white bold
  // title, and pill-ish caption buttons (red close). Hard-coded retro colors,
  // intentionally theme-independent (the body content matches via its own
  // classic skin). The manager forwards `skin` opaquely; only the view reacts.
  // ---------------------------------------------------------------------------
  frameClassic: {
    background: '#ece9d8',
    border: '1px solid #003c74', // XP active-window outer frame blue
    borderRadius: 8,
    boxShadow: '0 10px 34px rgba(0,0,0,0.45)',
    fontFamily: 'Tahoma, "MS Sans Serif", "Segoe UI", sans-serif',
    color: '#000',
  },
  // Even focused, keep the classic blue frame (no teal accent ring); just a
  // touch more shadow so the active window still reads as lifted.
  frameClassicFocused: {
    borderColor: '#003c74',
    boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
  },
  titleBarClassic: {
    // The signature Luna caption gradient (deep blue → bright blue).
    background: 'linear-gradient(180deg, #0997ff 0%, #0053ee 8%, #0050ee 40%, #06f 88%, #0997ff 100%)',
    borderBottom: 'none',
    padding: '3px 3px 3px 7px',
    color: '#fff',
  },
  titleClassic: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.8rem',
    textShadow: '1px 1px 1px rgba(0,0,0,0.5)',
  },
  // Square XP caption buttons. The base button is the light-blue raised chip;
  // close gets the red variant. Sizing tuned to look right next to the title.
  ctrlBtnClassic: {
    width: 21,
    height: 21,
    padding: 0,
    marginLeft: 2,
    color: '#fff',
    background: 'linear-gradient(180deg, #4fa6ff, #2061e8)',
    border: '1px solid #fff',
    borderRadius: 3,
    '& svg': { fontSize: '0.85rem' },
    '&:hover': {
      color: '#fff',
      background: 'linear-gradient(180deg, #79bcff, #3a78f0)',
    },
  },
  closeBtnClassic: {
    background: 'linear-gradient(180deg, #f0907f, #e0492f)',
    '&:hover': {
      color: '#fff',
      background: 'linear-gradient(180deg, #ffb0a0, #ef5a3a)',
    },
  },
  minimizeBtnClassic: {
    '& svg': {
      translate: '0px 5px',
      'width': '1.2em',
    }
  }

}));

// Clamp a rect so the window stays within the viewport (leaving EDGE_MARGIN and
// keeping the title bar reachable below the TopBar).
function clampRect({ x, y, w, h }) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cw = Math.min(w, vw - EDGE_MARGIN * 2);
  const ch = Math.min(h, vh - TOPBAR_H - EDGE_MARGIN);
  const cx = Math.min(Math.max(EDGE_MARGIN, x), Math.max(EDGE_MARGIN, vw - cw - EDGE_MARGIN));
  const cy = Math.min(Math.max(TOPBAR_H, y), Math.max(TOPBAR_H, vh - ch - EDGE_MARGIN));
  return { x: cx, y: cy, w: cw, h: ch };
}

// The rect a maximized window occupies. If the window opted into a maximize
// target (options.maximizeTarget = a CSS selector for a DOM region, e.g. the
// chat message column), the maximized window covers exactly that element's
// live bounds. Otherwise it falls back to the full area (viewport minus the
// TopBar, with a small bottom margin).
function maximizedRect(target) {
  if (target) {
    const el = document.querySelector(target);
    if (el) {
      const r = el.getBoundingClientRect();
      // Guard against a collapsed/hidden target (0 size) — fall through.
      if (r.width > 0 && r.height > 0) {
        return { x: r.left, y: r.top, w: r.width, h: r.height };
      }
    }
  }
  return {
    x: 0,
    y: TOPBAR_H,
    w: window.innerWidth,
    h: Math.max(120, window.innerHeight - TOPBAR_H - EDGE_MARGIN),
  };
}

const Window = ({ win, focused, handlers, children }) => {
  const { classes, cx } = useStyles();
  const frameRef = useRef(null);
  const animTimer = useRef(null);
  // Briefly toggled true around a maximize/restore so the geometry transitions.
  const [animating, setAnimating] = React.useState(false);

  const { title, maximized, options } = win;
  const minimizable = options?.minimizable !== false;
  const maximizable = options?.maximizable !== false;
  // Opt-in retro chrome. Forwarded opaquely by the manager; only this view
  // reacts to it. When set, the classic frame/title/buttons replace the
  // theme-driven ones (and the accent focus ring + title blink are dropped, as
  // they'd fight the fixed blue caption gradient).
  const classic = options?.skin === 'classic';

  // While maximized, the occupied rect is OBSERVED state, not computed once at
  // render — so it tracks ANY change to the target region (window resize, but
  // also a Reflex splitter drag or sidebar resize, which don't fire a window
  // 'resize' event). A ResizeObserver on the target element + a window resize
  // listener keep it in sync. `maxRect` is null until measured / when restored.
  const [maxRect, setMaxRect] = React.useState(null);
  useEffect(() => {
    if (!maximized) {
      setMaxRect(null);
      return undefined;
    }
    const measure = () =>
      setMaxRect((prev) => {
        const next = maximizedRect(options?.maximizeTarget);
        // Skip the state update if nothing actually moved (avoids re-render
        // churn during a continuous drag).
        if (
          prev &&
          prev.x === next.x &&
          prev.y === next.y &&
          prev.w === next.w &&
          prev.h === next.h
        ) {
          return prev;
        }
        return next;
      });
    measure();
    window.addEventListener('resize', measure);
    let ro;
    const targetEl = options?.maximizeTarget
      ? document.querySelector(options.maximizeTarget)
      : null;
    if (targetEl && typeof ResizeObserver !== 'undefined') {
      // Observing the target catches splitter/sidebar drags that resize the chat
      // region. measure() reads getBoundingClientRect, so a change that only
      // MOVES the target (without resizing it) is still picked up on the next
      // observer tick or window resize — good enough for our layouts.
      ro = new ResizeObserver(measure);
      ro.observe(targetEl);
    }
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [maximized, options?.maximizeTarget]);

  // Live geometry: maximized → the observed target rect (fall back to a fresh
  // read before the first observer tick); otherwise the manager-stored rect,
  // clamped to the viewport.
  const rect = maximized
    ? maxRect || maximizedRect(options?.maximizeTarget)
    : clampRect({ x: win.x, y: win.y, w: win.w, h: win.h });

  // Smoothly animate the maximize/restore geometry change.
  const triggerAnim = () => {
    setAnimating(true);
    clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => setAnimating(false), 220);
  };

  // Re-clamp a NON-maximized window on viewport resize so it never ends up
  // off-screen. (Maximized tracking is handled by the observer effect above.)
  useEffect(() => {
    if (win.maximized) return undefined;
    const onResize = () => {
      const c = clampRect({ x: win.x, y: win.y, w: win.w, h: win.h });
      if (c.x !== win.x || c.y !== win.y || c.w !== win.w || c.h !== win.h) {
        handlers.setRect(c);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [win.x, win.y, win.w, win.h, win.maximized, handlers]);

  // Move keyboard focus to the frame when this window becomes focused, so Esc /
  // Ctrl+W reach it and the focus ring is keyboard-reachable.
  useEffect(() => {
    if (focused && frameRef.current) {
      // Don't steal focus from an inner input the content may have focused.
      const active = document.activeElement;
      const insideThisWindow = frameRef.current.contains(active);
      if (!insideThisWindow) frameRef.current.focus({ preventScroll: true });
    }
  }, [focused]);

  const onKeyDown = (e) => {
    // Ctrl/Cmd+W closes (matches desktop). Esc closes when allowed.
    if ((e.ctrlKey || e.metaKey) && (e.key === 'w' || e.key === 'W')) {
      e.preventDefault();
      handlers.close();
      return;
    }
    if (e.key === 'Escape' && options?.escClose) {
      e.preventDefault();
      handlers.close();
    }
  };

  const doMaximize = () => {
    if (!maximizable) return;
    triggerAnim();
    handlers.maximize();
  };

  return (
    <Rnd
      className={cx(animating && classes.animating)}
      style={{ position: 'fixed', zIndex: win.z, display: 'flex' }}
      size={{ width: rect.w, height: rect.h }}
      position={{ x: rect.x, y: rect.y }}
      minWidth={options?.minWidth ?? 240}
      minHeight={options?.minHeight ?? 200}
      bounds="window"
      dragHandleClassName="wm-drag-handle"
      cancel=".wm-no-drag"
      disableDragging={maximized}
      enableResizing={!maximized}
      onDragStart={() => handlers.focus()}
      onDragStop={(e, d) => handlers.setRect({ x: d.x, y: d.y })}
      onResizeStop={(e, dir, refEl, delta, pos) =>
        handlers.setRect({
          x: pos.x,
          y: pos.y,
          w: parseInt(refEl.style.width, 10),
          h: parseInt(refEl.style.height, 10),
        })
      }
    >
      <div
        ref={frameRef}
        className={cx(
          classes.frame,
          focused && classes.frameFocused,
          classic && classes.frameClassic,
          classic && focused && classes.frameClassicFocused,
        )}
        role="dialog"
        aria-label={typeof title === 'string' ? title : 'Window'}
        tabIndex={-1}
        onMouseDown={() => handlers.focus()}
        onKeyDown={onKeyDown}
      >
        <div
          className={cx(
            classes.titleBar,
            'wm-drag-handle',
            classic && classes.titleBarClassic,
            // The attention blink would fight the fixed classic caption
            // gradient, so it's suppressed for the classic skin (the taskbar
            // tile still pulses).
            !classic && options?.attention && !focused && classes.titleBarAttention,
          )}
          onDoubleClick={doMaximize}
        >
          <span className={cx(classes.title, classic && classes.titleClassic)}>{title}</span>
          <div className={cx(classes.controls, 'wm-no-drag')}>
            {minimizable && (
              <IconButton
                className={cx(classes.ctrlBtn, classic && classes.ctrlBtnClassic, classes.minimizeBtnClassic)}
                size="small"
                onClick={handlers.minimize}
                aria-label="Minimize"
                title="Minimize"
              >
                <MinimizeIcon />
              </IconButton>
            )}
            {maximizable && (
              <IconButton
                className={cx(classes.ctrlBtn, classic && classes.ctrlBtnClassic)}
                size="small"
                onClick={doMaximize}
                aria-label={maximized ? 'Restore' : 'Maximize'}
                title={maximized ? 'Restore' : 'Maximize'}
              >
                {maximized ? <RestoreIcon /> : <MaximizeIcon />}
              </IconButton>
            )}
            <IconButton
              className={cx(
                classes.ctrlBtn,
                classes.closeBtn,
                classic && classes.ctrlBtnClassic,
                classic && classes.closeBtnClassic,
              )}
              size="small"
              onClick={handlers.close}
              aria-label="Close"
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className={classes.body}>{children}</div>
      </div>
    </Rnd>
  );
};

export default Window;
